"use client"

import React from 'react'
import { UseFormReturn } from 'react-hook-form'
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Separator } from "@/components/ui/separator"
import { Switch } from "@/components/ui/switch"
import { LayoutDashboard, Copy, Loader2, CheckCircle2 } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { FormSectionCard } from "./FormSectionCard"
import { useLanguage } from "@/contexts/language-context"
import { useFormSectionNavigation } from "@/hooks/use-form-section-navigation"
import { useEngagementLookups } from "@/hooks/use-engagement-lookups"
import { useOrganizations } from "@/hooks/use-organizations"
import { useBranches } from "@/hooks/use-branches"
import { useCodeValues } from "@/hooks/use-code-values"
import { CODE_TABLE_CATEGORIES } from "@/lib/constants/code-table-categories"
import { useEngagementDropdown } from "@/hooks/use-engagement-dropdown"
import { DebouncedInput } from "@/components/ui/debounced-input"
import { DatePicker } from "@/components/ui/date-picker"
import { type CoreFormData } from "@/lib/schemas"
import { ENGAGEMENT_TYPE, ENGAGEMENT_REQUEST_TYPE, ENGAGEMENT_STATUS } from "@/lib/constants/engagement-codes"
import { VirtualizedSelect } from "@/components/ui/virtualized-select"
import { InternalProfileSelect, ClientProfileSelect } from "@/components/ui/profile-selects"
import { FieldTooltip } from "@/components/ui/field-tooltip"
import type { WorkerProfileSummary, OrganizationSummary, EngagementReferenceSummary } from "@/types/api/engagement"
import type { Organization } from "@/hooks/use-organizations"
import { withRefFallback } from "@/lib/utils/reference-fallback"

export interface CoreSectionProps {
  form: UseFormReturn<CoreFormData>
  isCompleted: boolean
  isReadonly?: boolean
  isChangeRequest?: boolean
  /**
   * Server-resolved leaf status for the Original Engagement picker: false once another engagement
   * points at this one as its source, which the backend also refuses to let the pointer move past.
   * Deliberately not derived from isChangeRequest — a change request that nothing points at may still
   * be repointed. Defaults to false so the field fails closed if the flag is ever missing.
   */
  canEditEngagementSource?: boolean
  currentRecordId?: number
  hiddenSections: Record<string, boolean>
  /**
   * Server-resolved display names for engagement stakeholders, keyed by UserProfileId. Lets the
   * stakeholder pickers render an already-assigned profile for a restricted caller whose RLS-gated
   * profile lookups return nothing (see profile-selects withFallbackOption).
   */
  profileSummaries?: Record<number, WorkerProfileSummary>
  /**
   * Server-resolved org names (client org + internal company), keyed by OrganizationId. Lets the org
   * pickers render an already-assigned org for a restricted caller whose RLS-gated org list is empty.
   */
  organizations?: Record<number, OrganizationSummary>
  /** Server-resolved source-engagement names, keyed by RecordId — fallback for the Original Engagement picker. */
  sourceEngagements?: Record<number, EngagementReferenceSummary>
  /**
   * GW-198: copies the listed fields from the currently-selected Original Engagement into the form.
   * Resolves true on a successful copy (used to show the confirmation strip). Owned by the page so it
   * can reach every form section, not just core.
   */
  onCopyFromOriginal?: () => Promise<boolean>
  /** True while a copy is in flight — disables the button and shows a spinner. */
  isCopying?: boolean
}

export const CoreSection = ({
  form,
  isCompleted,
  isReadonly = false,
  isChangeRequest = false,
  canEditEngagementSource = false,
  currentRecordId,
  hiddenSections,
  profileSummaries,
  organizations,
  sourceEngagements,
  onCopyFromOriginal,
  isCopying = false,
}: CoreSectionProps) => {
  const { dictionary } = useLanguage()
  const navigationProps = useFormSectionNavigation('core', hiddenSections)
  const { getByCategory: getEngagementLookupsByCategory, getByCategoryUnsorted: getEngagementLookupsByCategoryUnsorted } = useEngagementLookups()

  // Get internal organizations using hook (consistent pattern)
  const {
    internalOrganizations: internalCompanies,
    clientOrganizations,
    loadingInternal: loadingInternalCompanies,
    loadingClient: loadingClientOrganizations
  } = useOrganizations()

  const watchedValues = form.watch(['engagementType', 'requestType', 'closedDate', 'selectedOriginalEngagement', 'selectedInternalCompany', 'selectedClient', 'selectedBranch'])
  const [engagementType, requestType, closedDate, selectedOriginalEngagement, selectedInternalCompany, selectedClient, selectedBranch] = watchedValues

  // Inject a synthetic option for an already-assigned reference that isn't in the RLS-gated list
  // (restricted caller), so the select still renders its label read-only. The server-resolved maps
  // supply the name. Editors (unrestricted) get the full list and never hit the fallback.
  const withOrgFallback = (orgs: Organization[], value: number | undefined): Organization[] =>
    withRefFallback(orgs, value, o => o.Id, (v) => {
      const summary = organizations?.[v]
      return summary?.displayName ? { Id: v, DisplayName: summary.displayName, Code: summary.code ?? '' } : null
    })

  const clientOrgOptions = React.useMemo(
    () => withOrgFallback(clientOrganizations, selectedClient),
    [clientOrganizations, selectedClient, organizations]
  )
  const internalCompanyOptions = React.useMemo(
    () => withOrgFallback(internalCompanies, selectedInternalCompany),
    [internalCompanies, selectedInternalCompany, organizations]
  )

  // Get active branches using hook (include current branch even if inactive for viewing)
  const { branches, loading: loadingBranches, error: branchesError, refresh: refreshBranches } = useBranches({
    includeInactiveBranchCode: selectedBranch || undefined
  })

  const { isReady: codeValuesReady, getCodeValueByCode } = useCodeValues()

  // The branch list (api/branch/list) is row-level-security gated, so a restricted caller doesn't
  // get the engagement's assigned branch back and the select would render blank. When the assigned
  // code is missing from that list, append a synthetic read-only option whose label comes from the
  // non-RLS branch code table — the same source the History view resolves branch from.
  // getCodeValueByCode is a stable callback, so codeValuesReady is in the deps to rebuild the option
  // once the global code table arrives (otherwise the label stays stuck on the raw code).
  const branchOptions = React.useMemo(() => {
    if (!selectedBranch || branches.some(b => b.Code === selectedBranch)) {
      return branches
    }
    const codeValue = getCodeValueByCode(selectedBranch, CODE_TABLE_CATEGORIES.BRANCH)
    return [...branches, { Id: 0, Code: selectedBranch, Name: codeValue?.text ?? selectedBranch, IsActive: false }]
  }, [branches, selectedBranch, getCodeValueByCode, codeValuesReady])

  // Hook for engagement dropdown (only when needed)
  const { options: engagementOptions } = useEngagementDropdown({
    selectedValue: selectedOriginalEngagement || undefined,
    excludeRecordId: currentRecordId
  })

  const engagementOptionsWithFallback = React.useMemo(
    () => withRefFallback(engagementOptions, selectedOriginalEngagement, o => o.value, (v) => {
      const summary = sourceEngagements?.[v]
      if (!summary) return null
      // Fall through per missing part so a code-without-name doesn't render a dangling "ENG-500 -".
      const label = summary.code && summary.name
        ? `${summary.code} - ${summary.name}`
        : (summary.code ?? summary.name ?? `${v}`)
      return { value: v, label, status: '' }
    }),
    [engagementOptions, selectedOriginalEngagement, sourceEngagements]
  )

  // A Completed engagement can be the Original Engagement but cannot be copied from: its worker rates
  // stopped following scheduled rate changes when it completed, and workers terminated since are still
  // on it. The reference fallback carries no status, so an unknown one leaves the button up and
  // handleCopyFromOriginal refuses the copy instead.
  const isOriginalEngagementCompleted = engagementOptionsWithFallback.some(
    option => option.value === selectedOriginalEngagement && option.status === ENGAGEMENT_STATUS.COMPLETED
  )

  // Trigger closedReason validation when closedDate changes
  React.useEffect(() => {
    // Trigger validation for closedReason when closedDate changes
    form.trigger('closedReason')
  }, [closedDate, form])

  // Confirmation strip: shown after a successful copy until the admin edits any core field.
  // The copy itself does a bulk reset() which fires watch; we ignore that initial burst by only
  // arming the clear-on-edit subscription after justCopied flips true.
  const [justCopied, setJustCopied] = React.useState(false)
  React.useEffect(() => {
    if (!justCopied) return
    const subscription = form.watch(() => setJustCopied(false))
    return () => subscription.unsubscribe()
  }, [justCopied, form])

  // GW-334: a copied worker's Engagement Start Date is taken from the engagement's own Start Date, so
  // copying before that is set leaves every worker without one. Confirm first rather than silently
  // handing back a form with a blank required date on each worker, so the admin can back out, set the
  // Start Date, and copy again to have the dates filled in for them.
  const [showNoStartDateWarning, setShowNoStartDateWarning] = React.useState(false)

  const runCopy = async () => {
    const ok = await onCopyFromOriginal?.()
    if (ok) setJustCopied(true)
  }

  const handleCopyClick = () => {
    if (!form.getValues('startDate')) {
      setShowNoStartDateWarning(true)
      return
    }
    void runCopy()
  }

  return (
    <FormSectionCard
      id="core"
      title={dictionary?.engagement?.core?.title || "Core"}
      description={dictionary?.engagement?.core?.description || "Manage core engagement details"}
      icon={LayoutDashboard}
      isCompleted={isCompleted}
      showPrevNext={true}
      {...navigationProps}
    >
      <Form {...form}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="engagementName"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
              {dictionary?.engagement?.core?.name || "Engagement Name"}
                </FormLabel>
                <FormControl>
                  <DebouncedInput
                    {...field}
                    placeholder={dictionary?.engagement?.core?.namePlaceholder || "Enter engagement name"}
                    readOnly={isReadonly}
                    className="form-field-focus"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="engagementCode"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
              {dictionary?.engagement?.core?.code || "Engagement Code"}
                </FormLabel>
                <FormControl>
                  <DebouncedInput
                    {...field}
                    placeholder={dictionary?.engagement?.core?.codePlaceholder || "Enter engagement code"}
                    readOnly={isReadonly}
                    className="form-field-focus"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Separator />

        <FormField
          control={form.control}
          name="engagementType"
          render={({ field }) => (
            <FormItem className="space-y-2.5">
              <FormLabel className="text-sm font-medium">
            {dictionary?.engagement?.core?.engagementType || "Engagement Type"}
              </FormLabel>
              <FormControl>
                <RadioGroup
                  {...field}
                  onValueChange={field.onChange}
                  disabled={isReadonly || isChangeRequest}
                  className="flex gap-6"
                >
                  {getEngagementLookupsByCategoryUnsorted('engagement.CodeEngagementType').map(workType => (
                    <div key={workType.code} className="flex items-center space-x-2">
                      <RadioGroupItem value={workType.code!} id={workType.code!} />
                      <Label htmlFor={workType.code!}>
                        {workType.value}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {engagementType === ENGAGEMENT_TYPE.ADVISORY && (
          <FormField
            control={form.control}
            name="requestType"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
              {dictionary?.engagement?.core?.requestType || "Request Type"}
                </FormLabel>
                <FormControl>
                  <RadioGroup
                    {...field}
                    onValueChange={field.onChange}
                    disabled={isReadonly}
                    className="flex gap-6"
                  >
                    {getEngagementLookupsByCategoryUnsorted('engagement.CodeEngagementRequestType').map(engType => (
                      <div key={engType.code} className="flex items-center space-x-2">
                        <RadioGroupItem value={engType.code!} id={engType.code!} />
                        <Label htmlFor={engType.code!}>
                          {engType.value}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {requestType === ENGAGEMENT_REQUEST_TYPE.CHANGE && engagementType === ENGAGEMENT_TYPE.ADVISORY && (
          <div className="space-y-2.5">
            <FormField
              control={form.control}
              name="selectedOriginalEngagement"
              render={({ field }) => (
                <FormItem className="space-y-2.5">
                  <FormLabel className="text-sm font-medium">
                    {dictionary?.engagement?.core?.engagementSource || "Original Engagement"}
                  </FormLabel>
                  <FormControl>
                    <VirtualizedSelect
                      {...field}
                      options={engagementOptionsWithFallback}
                      value={field.value ? String(field.value) : ""}
                      onValueChange={(value) => field.onChange(parseInt(value) || 0)}
                      getItemLabel={(item) => item.label}
                      getItemValue={(item) => String(item.value)}
                      placeholder={dictionary?.engagement?.core?.selectOriginalEngagement || "Select original engagement"}
                      searchPlaceholder="Search engagements..."
                      emptyMessage="No engagements found"
                      searchable={true}
                      disabled={isReadonly || !canEditEngagementSource}
                      className="form-field-focus"
                      allowClear
                    />
                  </FormControl>
                  {!canEditEngagementSource && !isReadonly && (
                    <p className="text-sm text-muted-foreground">
                      {dictionary?.engagement?.core?.engagementSourceLocked
                        || "Locked because other engagements list this one as their Original Engagement."}
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* One-shot copy from the selected Original Engagement. Only available while creating the
                initial version — once the engagement has been approved (isChangeRequest, i.e. version > 1)
                engagementType/client/billingType are locked, so we hide the copy entirely rather than let
                its unconditional reset() overwrite those locked fields. That is why it stays tied to
                isChangeRequest even though the picker beside it no longer is. Hidden on a read-only form
                too: a copy writes to every section, so there is nothing a viewer could do with the control.
                Enabled only once a source is picked; disabled while a copy is in flight. */}
            {!isChangeRequest && !isReadonly && !isOriginalEngagementCompleted && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopyClick}
                  disabled={isCopying || !selectedOriginalEngagement}
                  className="gap-2"
                >
                  {isCopying
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Copy className="h-4 w-4" />}
                  {dictionary?.engagement?.core?.copyFromEngagement || "Copy from this engagement"}
                </Button>

                {justCopied && (
                  <Alert className="border-green-600/40 bg-green-50 text-green-800">
                    <CheckCircle2 className="h-4 w-4 !text-green-700" />
                    <AlertDescription>
                      {dictionary?.engagement?.core?.copyConfirmation
                        || "Loaded workers, rates, tasks, client, etc. Edit any field below before submitting."}
                    </AlertDescription>
                  </Alert>
                )}

                <AlertDialog open={showNoStartDateWarning} onOpenChange={setShowNoStartDateWarning}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {dictionary?.engagement?.core?.copyNoStartDateTitle || "Start Date not set"}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {dictionary?.engagement?.core?.copyNoStartDateMessage
                          || "Copy sets each worker's Engagement Start Date to the later of two dates: their start date on the original engagement, and this engagement's Start Date. This engagement has no Start Date yet, so there is nothing to compare against and every worker will be copied with a blank start date. Cancel to set the Start Date first, then copy again."}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>
                        {dictionary?.engagement?.core?.copyNoStartDateCancel || "Cancel"}
                      </AlertDialogCancel>
                      <AlertDialogAction onClick={() => void runCopy()}>
                        {dictionary?.engagement?.core?.copyNoStartDateProceed || "Copy anyway"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </div>
        )}

        {engagementType !== ENGAGEMENT_TYPE.INTERNAL && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="selectedClient"
                render={({ field }) => (
                  <FormItem className="space-y-2.5">
                    <FormLabel className="text-sm font-medium">
                      {dictionary?.engagement?.core?.clientSelection || "Client Selection"}
                    </FormLabel>
                    <FormControl>
                      <VirtualizedSelect
                        {...field}
                        className="form-field-focus"
                        options={clientOrgOptions}
                        value={field.value ? String(field.value) : ""}
                        onValueChange={(value) => field.onChange(parseInt(value) || 0)}
                        getItemLabel={(org) => org.Code ? `${org.DisplayName} (${org.Code})` : org.DisplayName}
                        getItemValue={(org) => org.Id.toString()}
                        placeholder={dictionary?.engagement?.core?.selectClient || "Select client"}
                        searchPlaceholder="Search organizations..."
                        emptyMessage={loadingClientOrganizations ? "Loading organizations..." : undefined}
                        disabled={isReadonly || isChangeRequest}
                        allowClear
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="selectedContact"
                render={({ field }) => (
                  <FormItem className="space-y-2.5">
                    <FormLabel className="text-sm font-medium">
                      {dictionary?.engagement?.core?.clientContact || "Client Contact"}
                    </FormLabel>
                    <FormControl>
                      <ClientProfileSelect
                        {...field}
                        className="form-field-focus"
                        placeholder={dictionary?.engagement?.core?.selectContact || "Select contact"}
                        selectedClient={selectedClient}
                        isReadonly={isReadonly}
                        profileSummaries={profileSummaries}
                        allowClear
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

          </>
        )}

        <Separator />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
                  {dictionary?.engagement?.core?.startDate || "Start Date"}
                  <FieldTooltip content="Start of the timesheet period" />
                </FormLabel>
                <FormControl>
                  <DatePicker
                    {...field}
                    placeholder={dictionary?.engagement?.core?.pickDate || "Pick a date"}
                    disabled={isReadonly}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
                  {dictionary?.engagement?.core?.endDate || "End Date"}
                  <FieldTooltip content="End of the timesheet period" />
                </FormLabel>
                <FormControl>
                  <DatePicker
                    {...field}
                    placeholder={dictionary?.engagement?.core?.pickDate || "Pick a date"}
                    disabled={isReadonly}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="isPreEngagement"
            defaultValue={false}
            render={({ field }) => (
              <FormItem className="space-y-0 md:col-span-2">
                <div className="flex items-center gap-3">
                  <FormControl>
                    <Switch
                      checked={!!field.value}
                      onCheckedChange={(checked) => field.onChange(!!checked)}
                      disabled={isReadonly}
                    />
                  </FormControl>
                  <FormLabel className="text-sm font-medium cursor-pointer mb-0">
                    {dictionary?.engagement?.core?.isPreEngagement || "Pre-engagement"}
                  </FormLabel>
                </div>
                <p className="text-xs text-muted-foreground pt-3">
                  {dictionary?.engagement?.core?.isPreEngagementDescription || "Tag this engagement as scoping-phase work."}
                </p>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="agreementSignedDate"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
                  {dictionary?.engagement?.core?.agreementSigned || "Agreement Signed"}
                </FormLabel>
                <FormControl>
                  <DatePicker
                    {...field}
                    placeholder={dictionary?.engagement?.core?.pickDate || "Pick a date"}
                    disabled={isReadonly}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="closedDate"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
                  {dictionary?.engagement?.core?.closedDate || "Project Completion Date"}
                </FormLabel>
                <FormControl>
                  <DatePicker
                    {...field}
                    placeholder={dictionary?.engagement?.core?.pickDate || "Pick a date"}
                    disabled={isReadonly}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {closedDate && <FormField
            control={form.control}
            name="closedReason"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
                  {dictionary?.engagement?.core?.closedReason || "Closed Reason"}
                </FormLabel>
                <FormControl>
                  <Select 
                    {...field} 
                    onValueChange={field.onChange} 
                    disabled={isReadonly}
                    allowClear
                  >
                    <SelectTrigger className="form-field-focus">
                      <SelectValue placeholder={dictionary?.engagement?.core?.selectReason || "Select a reason"} />
                    </SelectTrigger>
                    <SelectContent>
                      {getEngagementLookupsByCategory('engagement.CodeEngagementClosedReason').map(reason => (
                        <SelectItem key={reason.code} value={reason.code!}>
                          {reason.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />}
        </div>

        <Separator />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="selectedInternalCompany"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
                  {dictionary?.engagement?.core?.internalCompany || "Internal Company"}
                </FormLabel>
                <FormControl>
                  <VirtualizedSelect
                    {...field}
                    className="form-field-focus"
                    options={internalCompanyOptions}
                    value={field.value ? String(field.value) : ""}
                    onValueChange={(value) => field.onChange(parseInt(value) || 0)}
                    getItemLabel={(org) => org.Code ? `${org.DisplayName} (${org.Code})` : org.DisplayName}
                    getItemValue={(org) => org.Id.toString()}
                    placeholder={dictionary?.engagement?.core?.selectCompany || "Select company"}
                    searchPlaceholder="Search companies..."
                    emptyMessage={loadingInternalCompanies ? "Loading companies..." : undefined}
                    disabled={isReadonly}
                    allowClear
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="selectedBranch"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
                  {dictionary?.engagement?.core?.branch || "Branch"}
                </FormLabel>
                <FormControl>
                  <Select 
                    {...field} 
                    onValueChange={field.onChange} 
                    disabled={isReadonly}
                    allowClear
                  >
                    <SelectTrigger className="form-field-focus">
                      <SelectValue placeholder={dictionary?.engagement?.core?.selectBranch || "Select branch"} />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingBranches ? (
                        <div className="p-2 text-sm text-muted-foreground">Loading branches...</div>
                      ) : branchOptions.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground">No active branches available</div>
                      ) : (
                        branchOptions.map(branch => (
                          <SelectItem key={branch.Code} value={branch.Code}>
                            {branch.Name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </FormControl>
                {branchesError && (
                  <p className="text-sm text-destructive">
                    Failed to load branches.{" "}
                    <Button 
                      type="button" 
                      onClick={refreshBranches} 
                      className="underline hover:no-underline"
                    >
                      Retry
                    </Button>
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {engagementType === ENGAGEMENT_TYPE.ADVISORY && (
            <>
              <FormField
                control={form.control}
                name="selectedClientExecutive"
                render={({ field }) => (
                  <FormItem className="space-y-2.5">
                    <FormLabel className="text-sm font-medium">
                      {dictionary?.engagement?.core?.clientExecutive || "Client Executive"}
                    </FormLabel>
                    <FormControl>
                      <InternalProfileSelect
                        {...field}
                        className="form-field-focus"
                        placeholder={dictionary?.engagement?.core?.selectClientExecutive || "Select client executive"}
                        selectedInternalCompany={selectedInternalCompany}
                        isReadonly={isReadonly}
                        profileSummaries={profileSummaries}
                        allowClear
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="selectedSalesPerson"
                render={({ field }) => (
                  <FormItem className="space-y-2.5">
                    <FormLabel className="text-sm font-medium">
                      {dictionary?.engagement?.core?.salesPerson || "Sales Person"}
                      <FieldTooltip content="Sales representative who closed the deal" />
                    </FormLabel>
                    <FormControl>
                      <InternalProfileSelect
                        {...field}
                        className="form-field-focus"
                        placeholder={dictionary?.engagement?.core?.selectSalesPerson || "Select sales person"}
                        selectedInternalCompany={selectedInternalCompany}
                        isReadonly={isReadonly}
                        profileSummaries={profileSummaries}
                        allowClear
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
          <FormField
            control={form.control}
            name="selectedEngagementLead"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
                  {dictionary?.engagement?.core?.engagementLead || "Engagement Lead"}
                </FormLabel>
                <FormControl>
                  <InternalProfileSelect
                    {...field}
                    className="form-field-focus"
                    placeholder={dictionary?.engagement?.core?.selectEngagementLead || "Select engagement lead"}
                    selectedInternalCompany={selectedInternalCompany}
                    isReadonly={isReadonly}
                    profileSummaries={profileSummaries}
                    allowClear
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="selectedProjectLead"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-medium">
                  {dictionary?.engagement?.core?.projectLead || "Delivery Manager"}
                </FormLabel>
                <FormControl>
                  <InternalProfileSelect
                    {...field}
                    className="form-field-focus"
                    placeholder={dictionary?.engagement?.core?.selectProjectLead || "Select delivery manager"}
                    selectedInternalCompany={selectedInternalCompany}
                    isReadonly={isReadonly}
                    profileSummaries={profileSummaries}
                    allowClear
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </Form>
    </FormSectionCard>
  )
}